
function bestSeperator(lines:string[]){
	let columnCounts:{[sep:string]:Map<number,number>}={
		',':new Map<number,number>(),
		';':new Map<number,number>(),
		'\t':new Map<number,number>(),
		' ':new Map<number,number>(),
	};

	for(let line of lines){
		for(let seperator in columnCounts){
			let columnCount=line.split(seperator).length;
			columnCounts[seperator].set(columnCount,(columnCounts[seperator].get(columnCount) || 0)+1);
		}
	}


	let bestSeperator:string;
	let bestSeperatorRating=-1;
	for(let seperator in columnCounts){
		let mostCommonColumnAppearenceCount=0;
		let mostCommonColumnsCount=0;
		for(let [columnsCount,appearenceCount] of columnCounts[seperator]){
			if(mostCommonColumnAppearenceCount<appearenceCount){
				mostCommonColumnAppearenceCount=appearenceCount;
				mostCommonColumnsCount=columnsCount;
			}
		}
		let rating=mostCommonColumnsCount*10000+mostCommonColumnAppearenceCount;
		if(bestSeperatorRating<rating){
			bestSeperatorRating=rating;
			bestSeperator=seperator;
		}
	}
	return bestSeperator;
}

type CsvRow=Array<string|number|boolean>;

export class Csv{
	public constructor(
		public headers:string[],
		public readonly rows:CsvRow[],
		public readonly rowLineNumbers:number[]=[],
	){
	}

	public columnCount(){
		return this.rows.reduce((maxColumns,row)=>Math.max(maxColumns,row.length),0);
	}

	public removeEmptyColumns(){
		//remove empty columns
		let columnCount=this.columnCount();
		for(let i=0;i<columnCount;){
			if(this.rows.every(row=>row[i]==='' || row[i]===undefined)){
				for(const row of this.rows){
					if(i<row.length)
						row.splice(i,1);
				}
				this.headers?.splice(i,1);
				columnCount-=1;
			}else
				++i;
		}
		return this;
	}

	public normalizeColumnCount(removeEmptyColumns:boolean){
		if(removeEmptyColumns)
			this.removeEmptyColumns();
		//make sure all rows have same number of columns remaining
		const columnCount=this.columnCount();
		for(let row of this.rows){
			while(row.length<columnCount)
				row.push('');
		}
		return this;
	}

	public addColumn(index:number, defaultValue:CsvRow[0]|((row:CsvRow)=>CsvRow[0]), header=''){
		this.headers.splice(index,0,header);
		if(typeof(defaultValue)==='function'){
			this.rows.forEach(row=>
				row.splice(index,0,defaultValue(row)));
		}else{
			this.rows.forEach(row=>
				row.splice(index,0,defaultValue));
		}
	}

	public [Symbol.iterator](){
		return this.rows.values();
	}

}

type csvConfig = {
	skipMetaInfoLines?: number,
	lowerCaseHeaders?: boolean
}

export namespace Csv{
	export type Row=CsvRow;

	const defaultConfig: csvConfig = {
		skipMetaInfoLines: undefined,
		lowerCaseHeaders: true
	}

	export function parse(csv:string, wantHeaders:boolean, config: csvConfig = defaultConfig){
		let tempCsv=csv.split('\n');
		if(config.skipMetaInfoLines)
			tempCsv=tempCsv.slice(config.skipMetaInfoLines);
		
		tempCsv=tempCsv.map(line=>line.trim());
		const lineNumbers=tempCsv.map((_,i)=>i).filter(i=>tempCsv[i].length>0);;
		const lines=tempCsv.filter(line=>line.length>0);
		const seperator=bestSeperator(lines);
		const rows=lines.map(line=>line.split(seperator).map(v=>v.trim()));
		const headers=wantHeaders?pullHeaders(rows, config.lowerCaseHeaders):null;
		return new Csv(headers,rows,lineNumbers);
	}

	export function build(args:{
		headers?:string[];
		rows:CsvRow[];
		delimiter?:string;
		quote?:string;
		escapedQuote?:string;
	}){
		const {rows,headers}=args;
		const delimiter=args.delimiter ?? ',';
		const quote=args.delimiter ?? '"';
		const escapedQuote=args.escapedQuote ?? (`${quote}${quote}`);

		function escapeString(str:string){
			if(str.includes(',')){
				str=str.replace(quote,escapedQuote);
				str=`${quote}${str}${quote}`;
				return str;
			}
			return str;
		}

		let columnCount=headers?.length ?? 0;
		for(const row of rows){
			if(columnCount<row.length)
				columnCount=row.length;
		}

		const out:string[][]=[];
		if(headers){
			while(headers.length<columnCount)
				headers.push('');
			out.push(headers.map(v=>escapeString(v)));
		}
		
		for(const inRow of rows){
			let outRow=inRow.map(v=>{
				if(typeof(v)==='string')
					return escapeString(v);
				return v?.toString() ?? '';
			});
			while(outRow.length<columnCount)
				outRow.push('');
			out.push(outRow);
		}
		return out.map(line=>line.join(delimiter)).join('\n');
	}
}

function pullHeaders(rows:string[][], lowerCaseHeaders:boolean){
	let fileHeaders:string[]=null;
	if(rows.length>0 && rows[0].reduce((c,v)=>isNaN(parseFloat(v))?c:c+1,0)<3){
		fileHeaders=rows[0].map(v=>lowerCaseHeaders ? v.toLowerCase() : v);
		rows.splice(0,1);
	}
	return fileHeaders;
}


